// ChartTooltip — the hover card for the SVG charts (BarChart, Treemap, LineChart).
//
// It sits BESIDE THE POINTER, not on top of it: offset to the pointer's right by
// default, flipping to its left when the card wouldn't fit in the space remaining.
// The side is decided from measured space — the card's own width against the
// distance to the viewport edge — so it behaves correctly at every width without
// any breakpoint of its own. It previously anchored to the hovered shape's
// top-centre, which put the card over the very bar or tile you were pointing at.
//
// INTERACTIVE MODE. A card that only reports should be inert and should chase the
// pointer. A card with a CONTROL in it must be reachable — and the two naive ways of
// doing that both fail:
//
//   • hit-testable while beside the pointer — a card 16px to the RIGHT is directly in
//     the path of every rightward sweep across a chart. The cursor lands in it, the
//     card stops tracking and shields the shapes underneath, and the tooltip reads as
//     stuck to the wrong bar. (This is the bug that produced this comment.)
//   • inert forever — the card keeps re-anchoring on the pointer, so its button stays
//     the same distance away however far you walk toward it. Unreachable.
//
// The fix has two halves, and neither works alone:
//
//   1. PLACE IT BELOW. An interactive card hangs under the pointer instead of beside
//      it, clear of the sweep line. Sweeping a chart is horizontal, so the cursor
//      never crosses the card and it tracks exactly as a reporting card does.
//   2. PARK ON DWELL. A card that follows the pointer can never be CAUGHT — it keeps
//      its offset, so walking toward it moves it away at the same speed, forever. So
//      when the pointer comes to rest (DWELL_MS) the card stops following and becomes
//      hit-testable. Now it can be walked into, and its button holds still under an
//      approaching hand.
//
// Half 2 alone was the original bug (a parked card beside the pointer eats the next
// sweep); half 1 alone leaves the button unreachable. Together: a sweep never touches
// the card (it is below the line, and moving un-parks it anyway), and a pause followed
// by a short move down lands you inside it.
//
// Parking is decided from GEOMETRY, not from a mouseenter: the walk from a shape into
// the card crosses the chart's own boundary (the card overhangs it), so a rule that
// depended on which element the browser reports under the pointer would drop the card
// mid-journey.
//
// Positioning is written straight to the DOM in a layout effect rather than held
// in state, and the pointer is tracked outside React. That matters: a 27-bar
// Recharts tree re-rendering on every pixel of pointer travel is the cost this
// design avoids. The node is `position: fixed`, so it also can't be clipped by a
// chart container's overflow.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import './chart-tooltip.css';

const GAP = 16; // breathing room between the pointer and the card's near edge
const EDGE = 8; // keep the card this far clear of the viewport edge
// How far BELOW the pointer an interactive card sits. Big enough that a hand holding
// a horizontal line never clips the card's top edge; small enough that reaching the
// card is one short, obvious move down.
const BELOW = 18;
// How long the pointer must rest before an interactive card parks. Short enough to
// feel immediate when you stop to read a tooltip; longer than any pause inside a
// sweep across a chart.
const DWELL_MS = 240;
// Slack around a PARKED card that still counts as "the pointer is on its way here".
// Must clear the card's own offset from the pointer (GAP across, BELOW down) or the
// gap between the two can never be crossed — see pointerNear().
const REACH = 28;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// ── Shared pointer tracker ──────────────────────────────────────────────────
// One capture-phase listener for however many cards are mounted, storing the last
// pointer position in a plain object. Capture phase so the position is already
// fresh by the time a chart's own mousemove handler asks us to reposition.
const pointer = { x: 0, y: 0 };
let trackers = 0;
const readPointer = (e) => {
  pointer.x = e.clientX;
  pointer.y = e.clientY;
};

/** Subscribe to pointer tracking; returns the unsubscribe. */
const trackPointer = () => {
  if (trackers === 0) {
    window.addEventListener('mousemove', readPointer, { passive: true, capture: true });
  }
  trackers += 1;
  return () => {
    trackers -= 1;
    if (trackers === 0) {
      window.removeEventListener('mousemove', readPointer, { capture: true });
    }
  };
};

export function ChartTooltip({ visible, children, className, onDismiss, interactive, onHold, onRelease }) {
  const ref = useRef(null);
  // Parked = the pointer is inside the card, so the card has stopped following and is
  // hit-testable. Mirrored in a ref because the mousemove handler reads it on every
  // pixel of pointer travel and must not re-subscribe to see a change.
  const [parked, setParked] = useState(false);
  const parkedRef = useRef(false);
  const dwellRef = useRef(null);

  const park = (on) => {
    parkedRef.current = on;
    setParked(on);
  };

  useEffect(trackPointer, []);

  // The card must not outlive the pointer resting on a bar/tile, and the chart's own
  // mouseleave can't guarantee that. SCROLLING fires no mouse event at all — the browser
  // only re-evaluates hover on the next pointer move — so a card left up while the page
  // moves keeps following the pointer, and being `position: fixed` it sails over whatever
  // is now under it (reported as a fund tooltip appearing over the By Fund table). The
  // pointer leaving the window entirely, and the window losing focus, are the same story.
  // Escape dismisses it too, as WCAG 1.4.13 requires of hover content.
  useEffect(() => {
    if (!visible || !onDismiss) return undefined;
    const dismiss = () => onDismiss();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    // Capture, because a scroll inside a chart container doesn't bubble.
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mouseleave', dismiss);
    window.addEventListener('blur', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, { capture: true });
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mouseleave', dismiss);
      window.removeEventListener('blur', dismiss);
    };
  }, [visible, onDismiss]);

  // Place the card next to the pointer. Imperative on purpose — see the file note.
  const place = () => {
    const el = ref.current;
    if (!el) return;
    // Parked: the pointer is at rest and the card is now a target. Moving it here
    // would walk the button out from under an approaching cursor.
    if (parkedRef.current) return;
    const { width: w, height: h } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer the right. Flip only when the right genuinely can't hold the card AND
    // the left has more room — so a card that fits nowhere (very narrow viewport)
    // stays on the roomier side and gets pulled inside the edge by the clamp.
    const roomRight = vw - EDGE - (pointer.x + GAP);
    const roomLeft = pointer.x - GAP - EDGE;
    const toLeft = roomRight < w && roomLeft > roomRight;

    const left = toLeft ? pointer.x - GAP - w : pointer.x + GAP;
    // A reporting card is centred on the pointer. An interactive one hangs BELOW it,
    // out of the horizontal sweep path — that is what keeps it from being walked into
    // by accident (see the file note). The clamp still wins near a viewport edge, so
    // a card with no room below flips above and stays reachable from the other side.
    const top = interactive ? pointer.y + BELOW : pointer.y - h / 2;
    el.style.left = `${clamp(left, EDGE, Math.max(EDGE, vw - EDGE - w))}px`;
    el.style.top = `${clamp(top, EDGE, Math.max(EDGE, vh - EDGE - h))}px`;
    // Exposed for styling/tests; the card itself is symmetric so nothing depends on it.
    el.dataset.side = toLeft ? 'left' : 'right';
    el.style.visibility = 'visible';
  };

  // No dep array: the card is re-measured after every render, because its height
  // changes with the content (a model tip has more rows than a fund tip) and the
  // vertical centring depends on that height.
  useLayoutEffect(() => {
    if (visible) place();
  });

  // Is the pointer within the card's box, allowing `margin` of slack? Asked of
  // geometry rather than of a mouseenter, because a parked card must let go even when
  // the pointer leaves it across a boundary the browser reports to the shape
  // underneath instead.
  //
  // The slack is load-bearing, not a fudge. A parked card sits BELOW+GAP away from
  // the pointer, so the pointer starts OUTSIDE it: with a strict test the first
  // movement toward the card un-parks it, it resumes following, and the gap can never
  // be crossed — the button stays permanently out of reach. REACH covers that
  // approach corridor, so a pointer heading for the card keeps it parked long enough
  // to arrive, while a pointer heading anywhere else is outside the corridor within a
  // few pixels and the card goes back to tracking.
  const pointerNear = (margin = 0) => {
    const el = ref.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return pointer.x >= r.left - margin && pointer.x <= r.right + margin
      && pointer.y >= r.top - margin && pointer.y <= r.bottom + margin;
  };

  // Follow the pointer, except while it is inside an interactive card.
  useEffect(() => {
    if (!visible) {
      // A card that goes away un-parks, or the next one would appear frozen at the
      // last card's coordinates — nowhere near the pointer that summoned it.
      clearTimeout(dwellRef.current);
      if (parkedRef.current) park(false);
      return undefined;
    }

    const scheduleDwell = () => {
      clearTimeout(dwellRef.current);
      if (!interactive) return; // a reporting card never parks
      dwellRef.current = setTimeout(() => park(true), DWELL_MS);
    };

    const onMove = () => {
      if (!interactive) {
        place(); // reporting card: always follows
        return;
      }
      // Already parked and still in the approach corridor: stay exactly where we are
      // and hold the chart's hover open, so the walk to the button can finish.
      if (parkedRef.current && pointerNear(REACH)) {
        onHold?.();
        return;
      }
      // Not parked yet, but the pointer is genuinely ON the card (it can get there
      // while the card is momentarily still): park now rather than jumping away.
      if (pointerNear(0)) {
        park(true);
        onHold?.();
        return;
      }
      // Left the corridor. Un-park, resume following, and tell the chart it may close
      // — it holds the answer to whether the pointer is still on a shape, and its
      // grace period keeps the card alive if the pointer has simply moved to the next
      // bar. Release is reported HERE, from geometry, and not from an onMouseOut on
      // the card: mouseout bubbles, so every boundary between two elements INSIDE the
      // card fired it and killed the card mid-walk.
      if (parkedRef.current) {
        park(false);
        onRelease?.();
      }
      place();
      scheduleDwell();
    };

    scheduleDwell();
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(dwellRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, interactive, onHold, onRelease]);

  if (!visible) return null;
  return (
    <div
      ref={ref}
      className={cx(
        'chart-tip',
        'chart-tip--floating',
        interactive && 'chart-tip--interactive',
        // Only a parked card is hit-testable — see the file note.
        interactive && parked && 'chart-tip--parked',
        className,
      )}
    >
      {children}
    </div>
  );
}

ChartTooltip.propTypes = {
  visible: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * Clear the caller's hover state. The card can't hide itself — the chart owns `visible` —
   * so this is how a scroll / Escape / pointer-left-the-window gets it off the screen.
   * Omit it and the card only closes when the chart's own mouseleave fires.
   */
  onDismiss: PropTypes.func,
  /**
   * The card contains a control, so it may PARK when the pointer rests: stop
   * following and become hit-testable. Leave it off for a card that only reports —
   * it stays inert and always tracks the pointer.
   */
  interactive: PropTypes.bool,
  /** Pointer is on (or approaching) the parked card — hold the chart's hover open. */
  onHold: PropTypes.func,
  /** Pointer left the card and its approach corridor — the chart may close it. */
  onRelease: PropTypes.func,
};

export default ChartTooltip;
